/**
 * Customer service requests -- the user's own list (scoped to them by JWT).
 *
 * Endpoints (verified against /openapi.json):
 *   GET  /api/v1/customer/service-requests/                       -> my SRs
 *   GET  /api/v1/customer/service-requests/{service_request_id}   -> my SR detail
 *   POST /api/v1/customer/service-requests/create/{service_type}  -> create new SR
 *   POST /api/v1/customer/service-requests/{id}/action2           -> submit documents phase
 *   GET  /api/v1/service-steps                                    -> static step catalog (public)
 */
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ApiError, createKeyFactory, type Schemas } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';
// Type-only (erased at runtime) — `service-closure.ts` imports our query keys.
import type { CustomerServiceClosure } from './service-closure';

// ---------- types (service requests) ----------------------------------------

/**
 * Service-request detail — augmented with the one-time rating fields returned
 * by `POST .../{id}/rate` (Jun 2026). The generated schema doesn't carry these
 * yet; drop the intersection once `pnpm gen:types` catches up to the backend.
 */
export type CustomerServiceRequestResponse = Omit<
  Schemas['CustomerServiceRequestResponse'],
  'service_state'
> & {
  rating?:   number | null;
  feedback?: string | null;
  /** INR amount the admin recorded as saved on this request (string, e.g.
   *  "25000.00"). `null` until set; meaningful once the request is COMPLETED. */
  money_saved?: string | null;
  /** `true` when an admin has an active payment gate on this request — the
   *  customer must pay before proceeding. Not in the generated schema yet;
   *  augment optionally until `pnpm gen:types` catches up. */
  payment_required?: boolean | null;
  /** Service Closure (Aug 2026): `ON_HOLD` was added to the state enum — a
   *  customer closure request is awaiting admin review. Widened locally until
   *  `pnpm gen:types` catches up. */
  service_state?: ServiceRequestStatus | null;
  /** Service Closure state + capability flags. The `can_*` flags are the only
   *  source of truth for closure buttons (see `api/service-closure.ts`). */
  service_closure?: CustomerServiceClosure | null;
  /** Multi-PAN: the PAN this request is filed under. */
  customer_pan_id?: string | null;
};
export type CustomerServiceRequestWorkflowState = Schemas['CustomerServiceRequestWorkflowState'];
/** Generated enum + `ON_HOLD` (Service Closure). */
export type ServiceRequestStatus = Schemas['ServiceRequestStatus'] | 'ON_HOLD';

/** `true` while the backend rejects customer mutations with 409 — a closure
 *  request is under review (`ON_HOLD`) or the service was closed (`CANCELLED`). */
export function isServiceLocked(sr: Pick<CustomerServiceRequestResponse, 'service_state' | 'service_closure'>): boolean {
  const state = sr.service_closure?.service_state ?? sr.service_state;
  return state === 'ON_HOLD' || state === 'CANCELLED';
}

/** Body for `POST /api/v1/customer/service-requests/{id}/rate`. */
export type RateServiceRequestPayload = { rating: number; feedback?: string };
/**
 * Service-creation payload — augmented for the v2 identity capture spec
 * (May 2026). PAN/DOB/customer_password are sent here when the customer
 * profile is missing them; backend saves the values back to the profile.
 *
 * Drop the intersection once `pnpm gen:types` catches up to the new
 * backend.
 */
export type ServiceRequestCreateRequest = Schemas['ServiceRequestCreateRequest'] & {
  customer_pan_id:             string;
  dob?:                        string | null;
  customer_password?:          string | null;
  /** "other" service flow. Narrows the generated `string | null` fields to
   *  `string | undefined` — the intake form always sends a trimmed string,
   *  never `null`. */
  custom_service_name?:        string;
  custom_service_description?: string;
};
export type RequiredDocumentItem = Schemas['RequiredDocumentItem'];
export type UploadedDocumentSummary = Schemas['UploadedDocumentSummary'];

// ---------- types (service steps) -------------------------------------------

export interface BackendServiceStep {
  step_number: number;
  title: string;
  description: string;
}

export interface ServiceStepsResponse {
  items: Record<string, BackendServiceStep[]>;
}

// ---------- query keys ------------------------------------------------------

export const serviceRequestKeys = createKeyFactory('customer.service-requests', {
  list:   (customerPanId?: string) => ['list', customerPanId ?? 'all'] as const,
  detail: (id: string) => ['detail', id] as const,
});

export const serviceStepsKeys = createKeyFactory('service-steps', {
  all: () => ['all'] as const,
});

// ---------- raw API functions -----------------------------------------------

/** GET /api/v1/customer/service-requests/ */
export async function listMyServiceRequests(signal?: AbortSignal, customerPanId?: string) {
  const { data } = await apiClient.get<CustomerServiceRequestResponse[]>(
    '/api/v1/customer/service-requests/',
    { signal, params: { customer_pan_id: customerPanId } },
  );
  return data;
}

/** GET /api/v1/customer/service-requests/{id} */
export async function getMyServiceRequest(id: string, signal?: AbortSignal) {
  const { data } = await apiClient.get<CustomerServiceRequestResponse>(
    `/api/v1/customer/service-requests/${id}`,
    { signal },
  );
  return data;
}

/** POST /api/v1/customer/service-requests/create/{service_type} */
export async function createServiceRequest(
  serviceTypeSnake: string,
  payload: ServiceRequestCreateRequest,
) {
  const { data } = await apiClient.post<CustomerServiceRequestResponse>(
    `/api/v1/customer/service-requests/create/${serviceTypeSnake}`,
    payload,
  );
  return data;
}

/** POST /api/v1/customer/service-requests/{id}/action2 */
export async function submitDocumentsPhase(id: string) {
  const { data } = await apiClient.post<CustomerServiceRequestResponse>(
    `/api/v1/customer/service-requests/${id}/action2`,
  );
  return data;
}

/**
 * POST /api/v1/customer/service-requests/{id}/rate — submit a one-time rating
 * (1–5) plus optional feedback for a completed service request. Returns the
 * updated SR with the stored rating. 409 if a rating was already submitted.
 */
export async function rateServiceRequest(id: string, payload: RateServiceRequestPayload) {
  const { data } = await apiClient.post<CustomerServiceRequestResponse>(
    `/api/v1/customer/service-requests/${id}/rate`,
    payload,
  );
  return data;
}

/** GET /api/v1/service-steps -- public, unauthenticated */
export async function fetchServiceSteps(signal?: AbortSignal) {
  const { data } = await apiClient.get<ServiceStepsResponse>(
    '/api/v1/service-steps',
    { signal },
  );
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useMyServiceRequests(
  customerPanId?: string,
  options?: Omit<UseQueryOptions<CustomerServiceRequestResponse[], ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<CustomerServiceRequestResponse[], ApiError>({
    queryKey: serviceRequestKeys.list(customerPanId),
    queryFn: ({ signal }) => listMyServiceRequests(signal, customerPanId),
    ...options,
  });
}

export function useMyServiceRequest(
  id: string,
  options?: Omit<UseQueryOptions<CustomerServiceRequestResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<CustomerServiceRequestResponse, ApiError>({
    queryKey: serviceRequestKeys.detail(id),
    queryFn: ({ signal }) => getMyServiceRequest(id, signal),
    enabled: Boolean(id),
    ...options,
  });
}

export function useCreateServiceRequest(
  options?: UseMutationOptions<
    CustomerServiceRequestResponse,
    ApiError,
    { serviceTypeSnake: string; payload: ServiceRequestCreateRequest }
  >,
) {
  return useMutation<
    CustomerServiceRequestResponse,
    ApiError,
    { serviceTypeSnake: string; payload: ServiceRequestCreateRequest }
  >({
    mutationFn: ({ serviceTypeSnake, payload }) =>
      createServiceRequest(serviceTypeSnake, payload),
    onSuccess: () => {
      // Prefix match (drop the trailing customerPanId/'all' segment) — busts
      // every PAN-scoped list bucket, not just the currently-active one.
      queryClient.invalidateQueries({ queryKey: ['customer.service-requests', 'list'] });
    },
    ...options,
  });
}

export function useSubmitDocumentsPhase(
  options?: UseMutationOptions<CustomerServiceRequestResponse, ApiError, string>,
) {
  return useMutation<CustomerServiceRequestResponse, ApiError, string>({
    mutationFn: submitDocumentsPhase,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: serviceRequestKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['customer.service-requests', 'list'] });
    },
    ...options,
  });
}

export function useRateServiceRequest(
  options?: UseMutationOptions<
    CustomerServiceRequestResponse,
    ApiError,
    { id: string; payload: RateServiceRequestPayload }
  >,
) {
  return useMutation<
    CustomerServiceRequestResponse,
    ApiError,
    { id: string; payload: RateServiceRequestPayload }
  >({
    mutationFn: ({ id, payload }) => rateServiceRequest(id, payload),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: serviceRequestKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['customer.service-requests', 'list'] });
    },
    ...options,
  });
}

/**
 * Fetches static step definitions for all service types from the backend.
 * Cached indefinitely (staleTime: Infinity) -- this is a static catalog.
 */
export function useServiceSteps(
  options?: Omit<UseQueryOptions<ServiceStepsResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ServiceStepsResponse, ApiError>({
    queryKey: serviceStepsKeys.all(),
    queryFn: ({ signal }) => fetchServiceSteps(signal),
    staleTime: Infinity,
    ...options,
  });
}

/** GET /api/v1/financial-years: newest first, next year through the past five. */
export async function listFinancialYears(signal?: AbortSignal) {
  const { data } = await apiClient.get<{ items: string[] }>('/api/v1/financial-years', {
    signal,
  });
  return data.items;
}

export function useFinancialYears(
  options?: Omit<UseQueryOptions<string[], ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<string[], ApiError>({
    queryKey: [...serviceRequestKeys.all, 'financial-years'],
    queryFn: ({ signal }) => listFinancialYears(signal),
    staleTime: 60 * 60_000,
    ...options,
  });
}
